import { useText } from "@/lib/content";
import { useEffect, useRef, useState } from "react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { validateFields, applySpinRules } from "@/lib/validators";
import { ConfigField } from "@/components/form/ConfigField";
import { ErrorSummary, Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { useRegistrationConfig, spinHintFor } from "@/pages/Register";

const CARDS = [
    ["Dati aziendali", ["ragione_sociale", "partita_iva", "settore", "fascia_dipendenti", "fascia_fatturato", "sito_web"]],
    ["Sede e contatti", ["provincia", "comune", "email", "telefono"]],
    ["Referente", ["nome_referente", "cognome_referente", "ruolo_referente"]],
];
const EDIT_ONLY = { "Sede e contatti": ["spin_id"] };

function displayValue(field, value, config) {
    if (!value) return "—";
    if (field.type === "province") {
        const p = config.provinces.find((x) => x.sigla === value);
        return p ? `${p.name} (${p.sigla})` : value;
    }
    if (field.type === "spin") return config.spins.find((s) => s.id === value)?.name || value;
    return value;
}

export const SectionTitle = ({ id, children, action }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-edi-border pb-3">
        <h2 id={id} className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <span className="h-7 w-1.5 rounded bg-edi-lime" aria-hidden="true" />{children}
        </h2>
        {action}
    </div>
);

const Rows = ({ fields, company, config }) => (
    <dl>
        {fields.map((f) => (
            <div key={f.key} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2.5 border-b border-edi-border last:border-0">
                <dt className="text-sm text-edi-muted">{f.label}</dt>
                <dd className="min-w-0 max-w-full sm:max-w-[62%] text-right font-medium text-edi-text [overflow-wrap:anywhere]" data-testid={`company-value-${f.key}`}>
                    {displayValue(f, company.profile[f.key], config)}
                </dd>
            </div>
        ))}
    </dl>
);

const Inputs = ({ fields, values, errors, onChange, config }) => (
    <div className="space-y-4">
        {fields.map((f) => (
            <ConfigField key={f.key} field={f} value={values[f.key]} error={errors[f.key]} onChange={onChange} provinces={config.provinces}
                spins={config.spins} readOnly={f.readonly_in_profile} spinHint={spinHintFor(config.spins, values.provincia)} />
        ))}
    </div>
);

const Card = ({ title, id, children, className = "" }) => (
    <section aria-labelledby={id} className={`edi-card p-5 min-w-0 ${className}`} data-testid={`company-card-${id}`}>
        <h3 id={id} className="text-xs font-bold uppercase tracking-[0.14em] text-edi-muted mb-2">{title}</h3>
        {children}
    </section>
);

export const CompanyDataSection = ({ company, onSaved, scrollHere, editing, setEditing }) => {
    const t = useText();
    const { config, error: loadError } = useRegistrationConfig();
    const ref = useRef(null);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (config && scrollHere) ref.current?.scrollIntoView({ block: "start" }); }, [config, scrollHere]);
    useEffect(() => {
        if (editing) { setValues({ ...company.profile }); setErrors({}); setStatus(null); }
    }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loadError) return <Notice kind="error" role="alert">{loadError}</Notice>;
    if (!config) return <Loading />;
    const fields = config.fields.filter((f) => f.in_profile);
    const byKey = (keys) => keys.map((k) => fields.find((f) => f.key === k)).filter(Boolean);
    const known = new Set([...CARDS.flatMap(([, keys]) => keys), "spin_id"]);
    const custom = fields.filter((f) => !known.has(f.key));
    const editable = fields.filter((f) => !f.readonly_in_profile);

    const cancel = () => { setEditing(false); setErrors({}); };
    const onChange = (key, value) => setValues((v) => {
        const next = { ...v, [key]: value };
        if (key === "provincia") next.spin_id = applySpinRules(config.spins, value);
        return next;
    });
    const save = async (e) => {
        e.preventDefault();
        setStatus(null);
        const errs = validateFields(editable, values);
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setBusy(true);
        try {
            const payload = Object.fromEntries(editable.map((f) => [f.key, values[f.key] ?? ""]));
            const { data } = await api.put("/companies/me", { values: payload });
            onSaved(data.company);
            setEditing(false);
            setStatus({ kind: "success", text: data.message });
        } catch (err) {
            const fe = fieldErrors(err);
            if (fe) setErrors(fe);
            else setStatus({ kind: "error", text: errorMessage(err, "Salvataggio non riuscito, riprova") });
        } finally {
            setBusy(false);
        }
    };

    const cards = CARDS.map(([title, keys], i) => {
        const list = byKey(editing ? [...keys, ...(EDIT_ONLY[title] || [])] : keys);
        const extra = i === 2 ? custom : [];
        const body = (fs) => (editing
            ? <Inputs fields={fs} values={values} errors={errors} onChange={onChange} config={config} />
            : <Rows fields={fs} company={company} config={config} />);
        return (
            <Card key={title} title={title} id={`card-${i}`} className={editing ? "" : "shrink-0 w-[85%] sm:w-[48%] lg:w-auto snap-start"}>
                {body(list)}
                {extra.length > 0 && (
                    <>
                        <h4 className="mt-4 mb-1 text-xs font-bold uppercase tracking-[0.14em] text-edi-muted">Altri dati</h4>
                        {body(extra)}
                    </>
                )}
            </Card>
        );
    });

    return (
        <section id="sezione-dati" ref={ref} aria-labelledby="dati-title" className="scroll-mt-6" data-testid="section-company-data">
            <h2 id="dati-title" className="sr-only">{t("profilo.data_title")}</h2>
            {status && <div className="mb-4"><Notice kind={status.kind} role={status.kind === "error" ? "alert" : "status"} testId="company-save-status">{status.text}</Notice></div>}
            {editing ? (
                <form noValidate onSubmit={save} className="space-y-5" data-testid="company-edit-form">
                    <ErrorSummary errors={errors} fields={fields} />
                    <div className="grid gap-5 lg:grid-cols-3 items-start">{cards}</div>
                    <div className="flex flex-wrap gap-3">
                        <button type="submit" className="btn-primary" disabled={busy} data-testid="company-save-button">Salva</button>
                        <button type="button" className="btn-tertiary" onClick={cancel} data-testid="company-cancel-button">Annulla</button>
                    </div>
                </form>
            ) : (
                <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 items-start lg:grid lg:grid-cols-3 lg:gap-5 lg:overflow-visible lg:pb-0"
                    tabIndex={0} role="region" aria-label="Dati dell'azienda" data-testid="company-data-list">{cards}</div>
            )}
        </section>
    );
};
